import { Link, useNavigate, useParams } from 'react-router-dom';
import { findCourseForUnit, findUnit } from '@/lib/content';
import { attemptStore, fluencyAttemptStore, sessionStore } from '@/lib/storage';
import { buildQuestions, EXAM_COUNT, EXAM_DURATION_MS } from '@/lib/quiz';
import type { QuizMode } from '@/types';

const EXAM_MINUTES = EXAM_DURATION_MS / 60000;

export default function UnitStart() {
  const { unitId = '' } = useParams();
  const unit = findUnit(unitId);
  const course = findCourseForUnit(unitId);
  const navigate = useNavigate();
  const best = unit ? attemptStore.bestScore(unit.id, (a) => a.mode !== 'readingcheck') : null;
  const rcBest = unit?.readingCheck
    ? attemptStore.bestScore(unit.id, (a) => a.mode === 'readingcheck')
    : null;

  if (!unit || !course) {
    return (
      <div className="empty-state">
        <h2>Unit not found</h2>
        <Link to="/" className="btn btn-secondary btn-sm" style={{ marginTop: 16 }}>← Home</Link>
      </div>
    );
  }

  const fBest = course.fluency ? fluencyAttemptStore.bestScore(course.slug) : null;

  const start = (mode: QuizMode) => {
    const questions = buildQuestions(unit, mode);
    sessionStore.set({
      unitId: unit.id,
      mode,
      startedAt: Date.now(),
      questions,
      answers: new Array(questions.length).fill(null),
      cur: 0,
      score: 0,
    });
    navigate(`/q/${unit.id}/play`);
  };

  if (unit.isExam) {
    const sources = unit.examUnits ?? [];
    return (
      <div className="start-screen">
        <div className="badge">{unit.courseCode} · Final Exam</div>
        <h1>Final <span>Exam</span></h1>
        <p className="subtitle">
          {EXAM_COUNT} questions drawn evenly across{' '}
          {sources.map((s) => `Unit ${s.unitNumber}`).join(', ')}, on a {EXAM_MINUTES}-minute clock.
        </p>

        <div className="stats">
          <div className="stat">
            <div className="stat-num">{EXAM_COUNT}</div>
            <div className="stat-label">Questions</div>
          </div>
          <div className="stat">
            <div className="stat-num">{EXAM_MINUTES}</div>
            <div className="stat-label">Minutes</div>
          </div>
          <div className="stat">
            <div className="stat-num">{unit.questions.length}</div>
            <div className="stat-label">In pool</div>
          </div>
        </div>
        {best && <div className="ver">Best so far: {best.pct}% ({best.score}/{best.total})</div>}

        <ul className="exam-rules">
          <li>Tap your answer, change it if you want, then check it for feedback.</li>
          <li>The clock survives a refresh and auto-submits at 0:00.</li>
          <li>Results break the score down by unit so you know where to go back.</li>
        </ul>

        <div className="start-buttons">
          <button className="btn btn-primary" onClick={() => start('final40')}>
            Start Final Exam →
          </button>
        </div>

        <Link to={`/c/${course.slug}`} className="back-link">← Back to {course.code}</Link>
      </div>
    );
  }

  if (unit.questions.length === 0) {
    return (
      <div className="start-screen">
        <div className="badge">{unit.courseCode} · FIT</div>
        <h1>Unit {unit.unitNumber}</h1>
        <p className="subtitle">{unit.unitTitle}</p>
        <div className="empty-unit-card">
          <h3>No questions yet</h3>
          <p>Add questions to <code>src/content/{course.slug}/unit{unit.unitNumber}.json</code> and they'll appear here automatically.</p>
        </div>
        <Link to={`/c/${course.slug}`} className="back-link">← Back to {course.code}</Link>
      </div>
    );
  }

  return (
    <>
      <header className="home-header">
        <div className="badge">{unit.courseCode} · Unit {unit.unitNumber}</div>
        <h1>{unit.unitTitle}</h1>
      </header>

      <div className="unit-list">
        {course.fluency && course.fluency.questions.length > 0 && (
          <Link to={`/f/${course.slug}`} className="unit-card fluency-card">
            <div className="uc-num">⚡ Fluency Quiz</div>
            <div className="uc-title">10 questions · 60 seconds</div>
            <div className="uc-meta">
              <span>{course.fluency.questions.length} in pool</span>
              {fBest && <span className="uc-best">Best: {fBest.pct}%</span>}
            </div>
          </Link>
        )}

        {unit.readingCheck && unit.readingCheck.questions.length > 0 && (
          <button
            className="unit-card readingcheck-card"
            onClick={() => start('readingcheck')}
            type="button"
          >
            <div className="uc-num">📖 Reading Check</div>
            <div className="uc-title">
              {unit.readingCheck.questionsPerSession} random questions from the readings
            </div>
            <div className="uc-meta">
              <span>{unit.readingCheck.questions.length} in pool</span>
              {rcBest && <span className="uc-best">Best: {rcBest.pct}%</span>}
            </div>
          </button>
        )}

        <div className="unit-card unit-quiz-card">
          <div className="uc-num">📝 Unit Quiz</div>
          <div className="uc-title">{unit.questions.length} questions in this unit</div>
          {best && (
            <div className="uc-meta">
              <span className="uc-best">Best: {best.pct}%</span>
            </div>
          )}
          <div className="uq-buttons">
            <button className="btn btn-primary" onClick={() => start('full')}>
              Full Quiz ({unit.questions.length}) →
            </button>
            <button className="btn btn-secondary" onClick={() => start('fit30')}>
              FIT-Style 30
            </button>
            <button className="btn btn-secondary" onClick={() => start('quick10')}>
              Quick 10
            </button>
          </div>
        </div>
      </div>

      <div className="course-back">
        <Link to={`/c/${course.slug}`} className="back-link">← Back to {course.code}</Link>
      </div>
    </>
  );
}
